import { NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, forwardRef, inject, Input, input, output, ChangeDetectionStrategy } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { AbstractControl, ControlValueAccessor, FormArray, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { isEqual } from 'lodash';

@Component({
  selector: 'iot-platform-ui-checkbox-group',
  imports: [TranslatePipe, FlexLayoutModule, ReactiveFormsModule, FormsModule, MatCheckboxModule, MatFormFieldModule, NgClass, MatIcon],
  templateUrl: './checkbox-group.component.html',
  styles: `
    .iot-platform-ui-checkbox-input--no-rows {
      min-height: 5rem;
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxGroupComponent),
      multi: true
    }
  ]
})
export class CheckboxGroupComponent implements ControlValueAccessor {
  items = input.required<any[]>();
  fxFlexOption = input<string>();
  capitalized = input<boolean>();
  disabled = input<boolean>(false);
  @Input() isOptionDisabled!: (item: any) => boolean;
  @Input() selectBy!: (item: any) => any;
  @Input() displayBy!: (item: any) => any;
  optionsControl: FormArray = new FormArray<FormControl<string>>([]);
  selectionChange = output<any[]>();
  private onTouched!: (_?: any) => void;
  private onChange!: (_?: any) => void;
  private readonly ref: ChangeDetectorRef = inject(ChangeDetectorRef);

  selectByFn(item: any) {
    return this.selectBy ? this.selectBy(item) : item;
  }

  displayByFn(item: any) {
    return this.displayBy ? this.displayBy(item) : item;
  }

  writeValue(values: any[]): void {
    this.optionsControl.clear();
    if (values && values?.length) {
      const items = this.items().filter((item) => !!values.find((e1) => isEqual(e1, this.selectByFn(item))));
      items.forEach((item) => {
        this.onSelectionChange(
          {
            checked: true,
            source: { value: this.selectByFn(item) }
          } as MatCheckboxChange,
          false
        );
      });
    }
    this.optionsControl.updateValueAndValidity();
    this.ref.detectChanges();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  isChecked(item: any): boolean {
    return this.optionsControl.getRawValue().includes(this.selectByFn(item));
  }

  onSelectionChange(event: MatCheckboxChange, emitEvent?: boolean): void {
    if (this.onTouched) {
      this.onTouched();
    }
    if (event.checked === true) {
      this.optionsControl.push(new FormControl<string>(event.source.value));
    } else {
      this.optionsControl.controls.forEach((control: AbstractControl, index: number) => {
        if (control.getRawValue() === event.source.value) {
          this.optionsControl.removeAt(index);
        }
      });
    }
    this.optionsControl.updateValueAndValidity();
    const values = this.optionsControl.getRawValue();
    if (emitEvent && this.onChange) {
      this.onChange(values);
      this.selectionChange.emit(values);
    }
  }
}
